import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { questionTypes } from 'app/frontend/types/custom/QuestionType'
import TextField from '../../../../components/form-fields/TextField'
import RichTextField from '../../../../components/form-fields/RichTextField'
import { uploadFile } from '../../../../api/team'
import CheckboxField from '../../../../components/form-fields/CheckboxField'
import { FormEntityContext } from '../../../../config/context/forms'
import { useTeam } from '../../../../config/context/team'
import { updateTeam } from '../../../../api/team'
import { displayErrorToast } from '../../../../helpers'
import QuestionsTable from '../../../TeamDashboard/subpages/TeamSettings/components/QuestionsTable'
import StepLayout from '../StepLayout'
import StepFooter from '../StepFooter'
import RecruitmentFormPreviewDialog from '../RecruitmentFormPreviewDialog'
import { StepProps } from '../types'

const formSchema = z.object({
  recruitmentDetails: z.object({
    show_country: z.boolean().optional(),
    show_age: z.boolean().optional(),
    introduction: z.string().optional(),
    submitted_introduction: z.string().optional(),
  }),
  questions_attributes: z.array(
    z.object({
      id: z.number().optional().or(z.null()),
      questionType: z.enum(questionTypes),
      description: z.string(),
      required: z.boolean(),
      order: z.number().optional(),
      _destroy: z.boolean().optional(),
      question_options_attributes: z
        .array(
          z.object({
            id: z.number().optional().or(z.null()),
            option: z.string(),
            _destroy: z.boolean().optional(),
          }),
        )
        .optional(),
    }),
  ),
})

type RecruitmentFormValues = z.infer<typeof formSchema>

const RecruitmentStep = ({ goNext, goBack, isFirst }: StepProps) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const form = useForm<RecruitmentFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      recruitmentDetails: {
        show_country: team.recruitmentDetails?.show_country ?? false,
        show_age: team.recruitmentDetails?.show_age ?? false,
        introduction: team.recruitmentDetails?.introduction ?? '',
        submitted_introduction:
          team.recruitmentDetails?.submitted_introduction ?? '',
      },
      questions_attributes:
        team.questions?.map((question) => ({
          ...question,
          question_options_attributes: question.questionOptions ?? [],
        })) ?? [],
    },
  })

  const onSubmit = (values: RecruitmentFormValues) => {
    setSubmitting(true)

    const payload = {
      recruitmentDetails: {
        ...team.recruitmentDetails,
        ...values.recruitmentDetails,
      },
      questions_attributes: values.questions_attributes.map(
        (question, index) => ({ ...question, order: index }),
      ),
    } as Partial<wowaudit.Team>

    updateTeam(team.id, payload)
      .then((updatedTeam) => {
        queryClient.setQueryData(['team', { path: team.path }], updatedTeam)
        setSubmitting(false)
        goNext()
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  const buildPreviewTeam = (): wowaudit.Team => {
    const values = form.getValues()

    return {
      ...team,
      recruitmentDetails: {
        ...team.recruitmentDetails,
        ...values.recruitmentDetails,
      } as wowaudit.Team['recruitmentDetails'],
      questions: values.questions_attributes
        .filter((question) => !question._destroy)
        .map((question, index) => ({
          id: question.id ?? -(index + 1),
          description: question.description,
          order: index,
          questionType: question.questionType,
          required: question.required,
          questionOptions: (question.question_options_attributes ?? [])
            .filter((option) => !option._destroy)
            .map((option, optionIndex) => ({
              id: option.id ?? -(optionIndex + 1),
              option: option.option,
            })),
        })),
    }
  }

  return (
    <StepLayout
      icon={UserPlus}
      title='Configure your recruitment form'
      description='Publish a polished recruitment form and evaluate candidates with confidence.'
    >
      <Form {...form}>
        <FormEntityContext.Provider
          value={{ entityType: 'team', i18nPrefix: 'forms.fields' }}
        >
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className='flex items-center justify-between gap-4 mb-4'>
              <h5 className='mb-0'>Custom questions</h5>

              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setPreviewOpen(true)}
              >
                Preview form
              </Button>
            </div>

            <QuestionsTable />

            <CheckboxField name='recruitmentDetails.show_country' />
            <CheckboxField name='recruitmentDetails.show_age' />

            <RichTextField
              name='recruitmentDetails.introduction'
              uploader={async (file) => (await uploadFile(team.id, file)).url}
            />

            <TextField
              name='recruitmentDetails.submitted_introduction'
              isMulti
            />

            <StepFooter
              showBack={!isFirst}
              onBack={goBack}
              onContinue={form.handleSubmit(onSubmit)}
              continueLoading={submitting}
              onSkip={goNext}
            />
          </form>
        </FormEntityContext.Provider>
      </Form>

      <RecruitmentFormPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        team={buildPreviewTeam()}
      />
    </StepLayout>
  )
}

export default RecruitmentStep
